import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import { Fotter, Header } from "../components/layout";
import { aboutUsImage } from "../assets";
import { useStateContext } from "../context/ContextProvider";

import {
  FaHeart,
  FaAward,
  FaLeaf,
  FaUtensils,
  FaQuoteRight,
} from "react-icons/fa";

import {
  GiHotSpices,
  GiMeal,
  GiCookingPot,
} from "react-icons/gi";

import {
  PiChefHatLight,
} from "react-icons/pi";

import {
  FiArrowLeft,
  FiArrowRight,
} from "react-icons/fi";

function AboutUs() {
  const { t } = useTranslation();
  const { currentDir } = useStateContext();

  const isRTL = currentDir === "rtl";

  const DirectionArrow = isRTL
    ? FiArrowLeft
    : FiArrowRight;

  const values = [
    {
      icon: GiMeal,
      number: "01",
      title: "authentic yemeni identity",
      description: "authentic yemeni identity description",
    },
    {
      icon: FaAward,
      number: "02",
      title: "quality in every detail",
      description: "quality in every detail description",
    },
    {
      icon: FaHeart,
      number: "03",
      title: "yemeni hospitality",
      description: "yemeni hospitality description",
    },
    {
      icon: GiHotSpices,
      number: "04",
      title: "flavors with character",
      description: "flavors with character description",
    },
  ];

  const experienceItems = [
    {
      icon: PiChefHatLight,
      title: "care in preparation",
      description: "care in preparation description",
    },
    {
      icon: FaLeaf,
      title: "selected ingredients",
      description: "selected ingredients description",
    },
    {
      icon: GiCookingPot,
      title: "authentic recipes",
      description: "authentic recipes description",
    },
  ];

  return (
    <>
      <Header active={2} DropActive={2} />

      <main
        className="
          overflow-hidden
          bg-back-primery
          dark:bg-back-darkprimery
        "
      >

        {/* =========================
            HERO
        ========================== */}

        <section
          className="
            relative
            pt-36
            pb-20
            md:pt-44
            md:pb-28
            bg-back-primery
            dark:bg-back-darkprimery
          "
        >
          {/* Decorative background */}

          <div
            className="
              absolute
              top-0
              end-0
              w-[420px]
              h-[420px]
              rounded-full
              bg-color-1/10
              blur-[120px]
              pointer-events-none
            "
          />

          <div
            className="
              absolute
              -bottom-40
              -start-40
              w-[380px]
              h-[380px]
              rounded-full
              border
              border-color-1/10
              pointer-events-none
            "
          />

          <div className="container relative z-10">

            <div
              className="
                grid
                lg:grid-cols-[0.95fr_1.05fr]
                gap-12
                lg:gap-20
                items-center
              "
            >

              {/* Image */}

              <motion.div
                initial={{
                  opacity: 0,
                  x: isRTL ? -50 : 50,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.8,
                }}
                className="
                  relative
                  order-2
                  lg:order-none
                "
              >

                {/* Outer frame */}

                <div
                  className="
                    absolute
                    -top-5
                    -end-5
                    w-full
                    h-full
                    rounded-[2.5rem]
                    border
                    border-color-1/40
                    hidden
                    md:block
                  "
                />

                <div
                  className="
                    relative
                    overflow-hidden
                    rounded-[2rem]
                    md:rounded-[2.5rem]
                    shadow-[0_30px_80px_rgba(68,37,20,0.18)]
                  "
                >
                  <img
                    src={aboutUsImage}
                    alt={t("Yaman restaurant interior")}
                    className="
                      w-full
                      h-[420px]
                      md:h-[560px]
                      lg:h-[620px]
                      object-cover
                      transition-transform
                      duration-[1500ms]
                      hover:scale-105
                    "
                  />

                  <div
                    className="
                      absolute
                      inset-0
                      bg-gradient-to-t
                      from-color-2/60
                      via-transparent
                      to-transparent
                    "
                  />
                </div>

                {/* Floating badge */}

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 25,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.7,
                    delay: 0.35,
                  }}
                  className="
                    absolute
                    -bottom-7
                    start-5
                    md:start-8
                    max-w-[270px]
                    rounded-[1.5rem]
                    bg-color-2
                    p-5
                    md:p-6
                    text-white
                    shadow-2xl
                  "
                >
                  <div
                    className="
                      flex
                      items-center
                      gap-3
                      mb-3
                    "
                  >
                    <div
                      className="
                        flex
                        w-11
                        h-11
                        items-center
                        justify-center
                        rounded-full
                        bg-color-1
                      "
                    >
                      <FaUtensils />
                    </div>

                    <span
                      className="
                        font-kufi
                        text-color-1
                        text-sm
                      "
                    >
                      {t("Yaman Restaurant")}
                    </span>
                  </div>

                  <p
                    className="
                      font-kufi
                      text-sm
                      leading-7
                      text-white/80
                    "
                  >
                    {t("yemeni taste in the heart of egypt")}
                  </p>
                </motion.div>

              </motion.div>

              {/* Content */}

              <motion.div
                initial={{
                  opacity: 0,
                  x: isRTL ? 50 : -50,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.8,
                }}
                className="
                  order-1
                  lg:order-none
                "
              >

                {/* Eyebrow */}

                <div
                  className="
                    inline-flex
                    items-center
                    gap-3
                    mb-7
                  "
                >
                  <span
                    className="
                      block
                      w-12
                      h-[2px]
                      bg-color-1
                    "
                  />

                  <span
                    className="
                      font-kufi
                      text-color-1
                      text-sm
                    "
                  >
                    {t("about Yaman")}
                  </span>
                </div>

                <h1
                  className="
                    font-kufi
                    font-bold
                    text-tcolor-4
                    dark:text-tcolor-1
                    text-[2.5rem]
                    leading-[1.45]
                    md:text-5xl
                    lg:text-[3.7rem]
                    lg:leading-[1.35]
                    mb-7
                    max-w-[700px]
                  "
                >
                  {t("from yemen to egypt a taste that tells a story")}
                </h1>

                <p
                  className="
                    font-kufi
                    text-n-6
                    dark:text-tcolor-2
                    text-base
                    md:text-lg
                    leading-9
                    max-w-[680px]
                    mb-9
                  "
                >
                  {t("about Yaman hero description")}
                </p>

                <Link
                  to="/MealsCategories"
                  className="
                    group
                    inline-flex
                    items-center
                    justify-center
                    gap-3
                    min-h-[58px]
                    px-8
                    rounded-full
                    bg-color-1
                    text-white
                    font-kufi
                    shadow-[0_15px_40px_rgba(165,119,58,0.25)]
                    transition-all
                    duration-300
                    hover:bg-color-2
                    hover:-translate-y-1
                  "
                >
                  <span>
                    {t("discover Yaman menu")}
                  </span>

                  <DirectionArrow
                    className="
                      text-lg
                      transition-transform
                      duration-300
                      group-hover:translate-x-1
                    "
                  />
                </Link>

              </motion.div>

            </div>

          </div>
        </section>


        {/* =========================
            STORY
        ========================== */}

        <section
          className="
            py-24
            md:py-32
            bg-back-secondary
            dark:bg-back-darksecondary
          "
        >
          <div className="container">

            <div
              className="
                relative
                overflow-hidden
                rounded-[2rem]
                md:rounded-[3rem]
                bg-color-2
                px-7
                py-12
                md:px-14
                md:py-16
                lg:px-20
                lg:py-20
              "
            >

              {/* Decorative circles */}

              <div
                className="
                  absolute
                  -top-24
                  -end-24
                  w-72
                  h-72
                  rounded-full
                  border
                  border-white/10
                "
              />

              <div
                className="
                  absolute
                  top-12
                  -end-12
                  w-40
                  h-40
                  rounded-full
                  bg-color-1/10
                  blur-2xl
                "
              />

              <div
                className="
                  relative
                  z-10
                  grid
                  lg:grid-cols-[0.7fr_1.3fr]
                  gap-12
                  lg:gap-20
                  items-start
                "
              >

                {/* Quote */}

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 35,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.7,
                  }}
                >
                  <FaQuoteRight
                    className="
                      text-color-1
                      text-4xl
                      mb-7
                    "
                  />

                  <p
                    className="
                      font-kufi
                      text-white
                      text-2xl
                      md:text-3xl
                      leading-[1.8]
                    "
                  >
                    {t("our table carries the spirit of yemen")}
                  </p>
                </motion.div>

                {/* Story */}

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 35,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.7,
                    delay: 0.1,
                  }}
                >

                  <span
                    className="
                      font-kufi
                      text-color-1
                      text-sm
                      block
                      mb-4
                    "
                  >
                    {t("our story")}
                  </span>

                  <h2
                    className="
                      font-kufi
                      font-bold
                      text-white
                      text-3xl
                      md:text-4xl
                      mb-7
                    "
                  >
                    {t("Yaman is more than a meal")}
                  </h2>

                  <p
                    className="
                      font-kufi
                      text-white/70
                      leading-9
                      mb-6
                    "
                  >
                    {t("Yaman story paragraph one")}
                  </p>

                  <p
                    className="
                      font-kufi
                      text-white/70
                      leading-9
                    "
                  >
                    {t("Yaman story paragraph two")}
                  </p>

                  <div
                    className="
                      mt-10
                      flex
                      flex-wrap
                      gap-3
                    "
                  >

                    <span
                      className="
                        rounded-full
                        border
                        border-white/15
                        bg-white/5
                        px-5
                        py-3
                        font-kufi
                        text-sm
                        text-white/80
                      "
                    >
                      {t("authentic yemeni flavors")}
                    </span>

                    <span
                      className="
                        rounded-full
                        border
                        border-white/15
                        bg-white/5
                        px-5
                        py-3
                        font-kufi
                        text-sm
                        text-white/80
                      "
                    >
                      {t("refined dining experience")}
                    </span>

                    <span
                      className="
                        rounded-full
                        border
                        border-white/15
                        bg-white/5
                        px-5
                        py-3
                        font-kufi
                        text-sm
                        text-white/80
                      "
                    >
                      {t("yemeni hospitality")}
                    </span>

                  </div>

                </motion.div>

              </div>

            </div>
          </div>
        </section>


        {/* =========================
            VALUES
        ========================== */}

        <section
          className="
            py-24
            md:py-32
            bg-back-primery
            dark:bg-back-darkprimery
          "
        >
          <div className="container">

            <motion.div
              initial={{
                opacity: 0,
                y: 25,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              className="
                max-w-3xl
                mb-14
                md:mb-20
              "
            >
              <span
                className="
                  font-kufi
                  text-color-1
                  text-sm
                  block
                  mb-4
                "
              >
                {t("what defines Yaman")}
              </span>

              <h2
                className="
                  h2
                  text-tcolor-4
                  dark:text-tcolor-1
                  mb-6
                "
              >
                {t("details that create the Yaman experience")}
              </h2>

              <p
                className="
                  body-2
                  text-n-6
                  dark:text-tcolor-2
                  leading-8
                "
              >
                {t("Yaman values description")}
              </p>
            </motion.div>

            <div
              className="
                grid
                sm:grid-cols-2
                lg:grid-cols-4
                gap-5
              "
            >

              {values.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{
                    opacity: 0,
                    y: 30,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                  }}
                  className="
                    group
                    relative
                    min-h-[330px]
                    overflow-hidden
                    rounded-[2rem]
                    border
                    border-color-1/15
                    bg-n-1
                    dark:bg-back-darksecondary
                    p-7
                    md:p-8
                    transition-all
                    duration-500
                    hover:-translate-y-2
                    hover:border-color-1/40
                    hover:shadow-[0_25px_60px_rgba(68,37,20,0.13)]
                  "
                >

                  <span
                    className="
                      absolute
                      top-5
                      end-6
                      font-kufi
                      text-5xl
                      font-bold
                      text-color-2/5
                      dark:text-white/5
                    "
                  >
                    {item.number}
                  </span>

                  <div
                    className="
                      flex
                      w-16
                      h-16
                      items-center
                      justify-center
                      rounded-2xl
                      bg-color-1/10
                      text-color-1
                      mb-9
                      transition-all
                      duration-500
                      group-hover:bg-color-1
                      group-hover:text-white
                      group-hover:rotate-3
                    "
                  >
                    <item.icon className="text-3xl" />
                  </div>

                  <h3
                    className="
                      font-kufi
                      text-xl
                      font-semibold
                      text-tcolor-4
                      dark:text-tcolor-1
                      mb-4
                    "
                  >
                    {t(item.title)}
                  </h3>

                  <p
                    className="
                      font-kufi
                      text-sm
                      text-n-6
                      dark:text-tcolor-2
                      leading-8
                    "
                  >
                    {t(item.description)}
                  </p>

                  <div
                    className="
                      absolute
                      bottom-0
                      start-0
                      w-0
                      h-[3px]
                      bg-color-1
                      transition-all
                      duration-500
                      group-hover:w-full
                    "
                  />

                </motion.article>
              ))}

            </div>

          </div>
        </section>


        {/* =========================
            EXPERIENCE
        ========================== */}

        <section
          className="
            py-24
            md:py-32
            bg-back-secondary
            dark:bg-back-darksecondary
          "
        >
          <div className="container">

            <div
              className="
                grid
                lg:grid-cols-[0.8fr_1.2fr]
                gap-12
                lg:gap-20
                items-center
              "
            >

              <motion.div
                initial={{
                  opacity: 0,
                  x: isRTL ? 40 : -40,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                viewport={{
                  once: true,
                }}
              >

                <span
                  className="
                    font-kufi
                    text-color-1
                    text-sm
                    block
                    mb-4
                  "
                >
                  {t("the Yaman experience")}
                </span>

                <h2
                  className="
                    h2
                    text-tcolor-4
                    dark:text-tcolor-1
                    mb-6
                  "
                >
                  {t("authenticity you taste in every detail")}
                </h2>

                <p
                  className="
                    body-2
                    text-n-6
                    dark:text-tcolor-2
                    leading-9
                  "
                >
                  {t("Yaman experience description")}
                </p>

              </motion.div>

              <div className="space-y-4">

                {experienceItems.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{
                      opacity: 0,
                      x: isRTL ? -35 : 35,
                    }}
                    whileInView={{
                      opacity: 1,
                      x: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                    }}
                    className="
                      group
                      flex
                      items-start
                      gap-5
                      rounded-[1.75rem]
                      border
                      border-color-1/15
                      bg-n-1
                      dark:bg-back-darkprimery
                      p-5
                      md:p-6
                      transition-all
                      duration-300
                      hover:border-color-1/40
                      hover:translate-x-1
                    "
                  >

                    <div
                      className="
                        flex
                        w-14
                        h-14
                        md:w-16
                        md:h-16
                        shrink-0
                        items-center
                        justify-center
                        rounded-2xl
                        bg-color-2
                        text-color-1
                      "
                    >
                      <item.icon className="text-2xl md:text-3xl" />
                    </div>

                    <div>
                      <h3
                        className="
                          font-kufi
                          text-lg
                          md:text-xl
                          font-semibold
                          text-tcolor-4
                          dark:text-tcolor-1
                          mb-2
                        "
                      >
                        {t(item.title)}
                      </h3>

                      <p
                        className="
                          font-kufi
                          text-sm
                          text-n-6
                          dark:text-tcolor-2
                          leading-7
                        "
                      >
                        {t(item.description)}
                      </p>
                    </div>

                  </motion.div>
                ))}

              </div>

            </div>

          </div>
        </section>


        {/* =========================
            FINAL CTA
        ========================== */}

        <section
          className="
            py-20
            md:py-28
            bg-back-primery
            dark:bg-back-darkprimery
          "
        >
          <div className="container">

            <motion.div
              initial={{
                opacity: 0,
                y: 30,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              className="
                relative
                overflow-hidden
                rounded-[2rem]
                md:rounded-[3rem]
                bg-color-2
                px-7
                py-14
                md:px-14
                md:py-20
                text-center
              "
            >

              <div
                className="
                  absolute
                  -top-40
                  start-1/2
                  -translate-x-1/2
                  w-[500px]
                  h-[500px]
                  rounded-full
                  bg-color-1/10
                  blur-[100px]
                "
              />

              <div className="relative z-10">

                <GiMeal
                  className="
                    mx-auto
                    text-color-1
                    text-5xl
                    mb-6
                  "
                />

                <h2
                  className="
                    font-kufi
                    font-bold
                    text-white
                    text-3xl
                    md:text-5xl
                    leading-[1.5]
                    mb-6
                  "
                >
                  {t("your Yemeni experience starts at Yaman")}
                </h2>

                <p
                  className="
                    font-kufi
                    text-white/70
                    max-w-2xl
                    mx-auto
                    leading-8
                    mb-9
                  "
                >
                  {t("final Yaman call to action description")}
                </p>

                <Link
                  to="/MealsCategories"
                  className="
                    group
                    inline-flex
                    items-center
                    justify-center
                    gap-3
                    rounded-full
                    bg-color-1
                    px-8
                    py-4
                    font-kufi
                    text-white
                    transition-all
                    duration-300
                    hover:bg-white
                    hover:text-color-2
                  "
                >
                  {t("explore our dishes")}

                  <DirectionArrow
                    className="
                      text-lg
                      transition-transform
                      duration-300
                      group-hover:translate-x-1
                    "
                  />
                </Link>

              </div>

            </motion.div>

          </div>
        </section>

      </main>

      <Fotter />
    </>
  );
}

export default AboutUs;